import { configureLlm, LlmTransportError, type LlmProvider } from '../../llm/client.js';
import type { CharacterBond } from '../../domain/types.js';
import { isCharacterPresent } from '../../runtime/visitPresence.js';
import { blankRelationship } from '../../sim/relationships.js';
import type { Npc, Relationship } from '../../types.js';
import { flipNeurotic, proceduralNpc, spawnNpcViaLlm, uid } from '../spawn.js';
import { initWorld, type WorldState } from '../useSim.js';
import type { WorldLaunchHandoff } from '../CreateWorld/draftStorage.js';
import { appendNpcToWorldState } from './appendNpc.js';
import { initAquariumWorld } from './gameIntegration.js';

export async function spawnGameWorld(args: {
  isAquarium: boolean;
  handoff: WorldLaunchHandoff | null;
  provider: LlmProvider;
  apiKeys: string[];
  model: string;
  count: number;
  nowIso?: string;
  roster?: Npc[];
  onProgress: (spawned: number) => void;
}): Promise<{ npcs: Npc[]; world: WorldState; spawnOffline: boolean }> {
  const {
    isAquarium, handoff, provider, apiKeys, model, count, nowIso, roster, onProgress,
  } = args;

  configureLlm(provider, apiKeys, model);
  if (isAquarium && handoff) {
    const clockIso = nowIso ?? handoff.snapshot.clock.inWorldIso ?? new Date().toISOString();
    const initialRoster = handoff.roster.filter((_, index) => {
      const character = handoff.snapshot.characters[index];
      return !character || isCharacterPresent(clockIso, character.visitor, character.visits);
    });
    const { npcs, world } = initAquariumWorld(initialRoster);
    onProgress(npcs.length);
    return { npcs, world, spawnOffline: false };
  }

  const list: Npc[] = (roster ?? []).slice(0, count).map((n) => ({ ...n }));
  const taken = new Set(list.map((n) => `${n.firstName} ${n.lastName}`));
  onProgress(list.length);
  let transportFails = 0;
  let generated = 0;
  for (let i = list.length; i < count; i++) {
    const id = uid('npc');
    const neurotic = flipNeurotic();
    try {
      const npc = await spawnNpcViaLlm(id, neurotic, [...taken]);
      list.push(npc);
      taken.add(`${npc.firstName} ${npc.lastName}`);
    } catch (e) {
      if (e instanceof LlmTransportError) transportFails++;
      const fb = proceduralNpc(id, neurotic, taken);
      list.push(fb);
      taken.add(`${fb.firstName} ${fb.lastName}`);
    }
    generated++;
    onProgress(i + 1);
  }
  return { npcs: list, world: initWorld(list), spawnOffline: generated > 0 && transportFails === generated };
}

/** Keep the live aquarium roster aligned with timer-window visitor presence. */
export function syncAquariumVisitorPresence(
  handoff: WorldLaunchHandoff,
  currentNpcs: Npc[],
  world: WorldState | null,
  nowIso: string,
  relationships = new Map<string, Relationship>(),
): { npcs: Npc[]; world: WorldState | null } {
  const presentAt = (index: number): boolean => {
    const character = handoff.snapshot.characters[index];
    return !character || isCharacterPresent(nowIso, character.visitor, character.visits);
  };
  const rosterIndex = new Map(handoff.roster.map((npc, index) => [npc.id, index]));
  let npcs = currentNpcs.filter((npc) => {
    const index = rosterIndex.get(npc.id);
    return index === undefined || presentAt(index);
  });
  let nextWorld = world;
  const liveIds = new Set(npcs.map((npc) => npc.id));
  for (let index = 0; index < handoff.roster.length; index++) {
    const npc = handoff.roster[index];
    if (!npc || !presentAt(index) || liveIds.has(npc.id)) continue;
    const added = appendNpcToWorldState(npcs, nextWorld, relationships, npc);
    npcs = added.npcs;
    nextWorld = added.world;
    liveIds.add(npc.id);
  }
  if (nextWorld) {
    const ids = new Set(npcs.map((npc) => npc.id));
    nextWorld = {
      ...nextWorld,
      npcs,
      runtime: nextWorld.runtime.filter((runtime) => ids.has(runtime.npcId)),
    };
  }
  return { npcs, world: nextWorld };
}

const relKey = (a: string, b: string): string => `${a}>${b}`;

export function seedBidirectionalRelationships(rels: Map<string, Relationship>, list: Npc[]): void {
  for (const a of list) {
    for (const b of list) {
      if (a.id !== b.id) rels.set(relKey(a.id, b.id), blankRelationship(a.id, b.id));
    }
  }
}

/** Apply wizard snapshot bonds onto seeded relationship rows (directional only). */
export function applyCharacterBonds(
  rels: Map<string, Relationship>,
  roster: Npc[],
  characters: Array<{ draftCharacterId: string; status: string }>,
  bonds: CharacterBond[],
): void {
  const active = characters.filter((c) => c.status !== 'removed');
  const draftToNpc = new Map<string, string>();
  active.forEach((c, i) => {
    const npc = roster[i];
    if (npc) draftToNpc.set(c.draftCharacterId, npc.id);
  });
  for (const bond of bonds) {
    const fromNpc = draftToNpc.get(bond.fromId);
    const toNpc = draftToNpc.get(bond.toId);
    if (!fromNpc || !toNpc) continue;
    const key = relKey(fromNpc, toNpc);
    const existing = rels.get(key);
    if (!existing) continue;
    rels.set(key, {
      ...existing,
      familiarity: bond.closeness,
      bondKind: bond.kind,
    });
  }
}

export function applyResumePose(
  world: WorldState,
  facingAway?: [string, string],
): WorldState {
  if (!facingAway || facingAway.length !== 2) return world;
  const [first, second] = facingAway;
  return {
    ...world,
    runtime: world.runtime.map((runtime) => {
      if (runtime.npcId === first) return { ...runtime, dir: 'left' as const };
      if (runtime.npcId === second) return { ...runtime, dir: 'right' as const };
      return runtime;
    }),
  };
}
